import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link2, Loader2, Upload } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@cvx/_generated/api";
import { useConvexMutation } from "@convex-dev/react-query";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

interface PartnershipDocumentUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnershipId: Id<"creatorContracts">;
}

interface UploadResponse {
  storageId: Id<"_storage">;
}

export function PartnershipDocumentUpload({
  open,
  onOpenChange,
  partnershipId,
}: PartnershipDocumentUploadProps) {
  const [activeTab, setActiveTab] = useState("upload");
  const [documentName, setDocumentName] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);
  const { mutateAsync: addDocument } = useMutation({
    mutationFn: useConvexMutation(api.partnerships.addDocumentToPartnership),
  });

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      try {
        setIsSubmitting(true);
        const uploadResponse = uploaded[0].response as UploadResponse;
        const fileName = documentName || uploaded[0].name;
        await addDocument({
          partnershipId,
          name: fileName,
          url: uploadResponse.storageId,
        });
        toast({
          title: "Document uploaded",
          description: "The document has been uploaded successfully",
        });
        onOpenChange(false);
      } catch (error) {
        toast({
          title: "Error uploading document",
          description: "There was an error uploading the document",
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
        setIsUploading(false);
      }
    },
    onUploadError: () => {
      setIsUploading(false);
      toast({
        title: "Error uploading document",
        description: "There was an error uploading the document",
        variant: "destructive",
      });
    },
  });

  const handleUrlSubmit = async () => {
    if (!documentUrl) {
      toast({
        title: "Missing URL",
        description: "Please provide a URL for the document",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      // Extract filename from URL if no name provided
      const urlObj = new URL(documentUrl);
      const fallbackName = urlObj.pathname.split("/").pop() || "document";

      await addDocument({
        partnershipId,
        name: documentName || fallbackName,
        url: documentUrl,
      });
      toast({
        title: "Document added",
        description: "The document has been added successfully",
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error adding document",
        description: "There was an error adding the document",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Document</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">Upload File</TabsTrigger>
            <TabsTrigger value="link">Add Link</TabsTrigger>
          </TabsList>

          <div className="mt-4">
            <div className="mb-4">
              <Label htmlFor="documentName">Document Name (Optional)</Label>
              <Input
                id="documentName"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="Leave empty to use filename"
              />
            </div>

            <TabsContent value="upload" className="mt-0">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-6">
                  {isUploading ? (
                    <>
                      <Loader2 className="mb-2 h-8 w-8 animate-spin text-primary/60" />
                      <p className="mb-1 text-sm font-medium">
                        Uploading file...
                      </p>
                      <p className="mb-4 text-xs text-primary/60">
                        Please wait while we process your file
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="mb-2 h-8 w-8 text-primary/60" />
                      <p className="mb-1 text-sm font-medium">Upload a file</p>
                      <p className="mb-4 text-xs text-primary/60">
                        Drag and drop or click to select
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length === 0) return;
                          setIsUploading(true);
                          startUpload(files);
                        }}
                      />
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Select File
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="link" className="mt-0">
              <div className="flex flex-col gap-4">
                <div>
                  <Label htmlFor="documentUrl">Document URL</Label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Link2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/60" />
                      <Input
                        id="documentUrl"
                        className="pl-9"
                        value={documentUrl}
                        onChange={(e) => setDocumentUrl(e.target.value)}
                        placeholder="https://"
                      />
                    </div>
                  </div>
                </div>
                <Button
                  className="w-full"
                  disabled={isSubmitting}
                  onClick={handleUrlSubmit}
                >
                  {isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Add Document
                </Button>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
